import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { PageLoader } from '../components/feedback/PageLoader';
import { PublicLayout } from '../layouts/PublicLayout';
import { NotFoundPage } from '../pages/NotFoundPage';

const CompetitionPage = lazy(() => import('../pages/CompetitionPage'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const ComplaintsFeedbackPage = lazy(() => import('../pages/ComplaintsFeedbackPage'));
const FaqPage = lazy(() => import('../pages/FaqPage'));
const HomePage = lazy(() => import('../pages/HomePage'));
const LegalPage = lazy(() => import('../pages/LegalPage'));
const OperatorPage = lazy(() => import('../pages/OperatorPage'));
const CertificateVerificationPage = lazy(() => import('../pages/CertificateVerificationPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const RegistrationSuccessPage = lazy(() => import('../pages/RegistrationSuccessPage'));
const PaymentReturnPage = lazy(() => import('../pages/PaymentReturnPage'));
const ParticipantCertificatesPage = lazy(() => import('../pages/portal/ParticipantCertificatesPage'));
const ParticipantDashboardPage = lazy(() => import('../pages/portal/ParticipantDashboardPage'));
const ParticipantNotificationsPage = lazy(() => import('../pages/portal/ParticipantNotificationsPage'));
const ParticipantProfilePage = lazy(() => import('../pages/portal/ParticipantProfilePage'));
const ParticipantResultsPage = lazy(() => import('../pages/portal/ParticipantResultsPage'));
const CertificatePrintPage = lazy(() => import('../pages/CertificatePrintPage'));
const ParticipantSignInPage = lazy(() => import('../pages/portal/ParticipantSignInPage'));
const ParticipantQuizStartPage = lazy(() => import('../pages/portal/ParticipantQuizStartPage'));
const ParticipantQuizPage = lazy(() => import('../pages/portal/ParticipantQuizPage'));
const ParticipantQuizSubmittedPage = lazy(() => import('../pages/portal/ParticipantQuizSubmittedPage'));
const ParticipantPortalBoundary = lazy(() => import('../layouts/ParticipantPortalBoundary').then((module) => ({ default: module.ParticipantPortalBoundary })));
const RulesPage = lazy(() => import('../pages/RulesPage'));
const AdminSignInPage = lazy(() => import('../pages/admin/AdminSignInPage'));
const AdminQuestionsPage = lazy(() => import('../pages/admin/AdminQuestionsPage'));
const AdminQuestionEditorPage = lazy(() => import('../pages/admin/AdminQuestionEditorPage'));
const AdminQuestionImportPage = lazy(() => import('../pages/admin/AdminQuestionImportPage'));
const AdminOperationsPage = lazy(() => import('../pages/admin/AdminOperationsPage'));
const AdminIndexPage = lazy(() => import('../pages/admin/AdminIndexPage'));
const AdminWorkspacePage = lazy(() => import('../pages/admin/AdminWorkspacePage'));
const AdminBoundary = lazy(() => import('../layouts/AdminBoundary').then((module) => ({ default: module.AdminBoundary })));

function loading(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: loading(<HomePage />) },
      { path: '/competition', element: loading(<CompetitionPage />) },
      { path: '/rules', element: loading(<RulesPage />) },
      { path: '/faq', element: loading(<FaqPage />) },
      { path: '/contact', element: loading(<ContactPage />) },
      { path: '/feedback', element: loading(<ComplaintsFeedbackPage />) },
      { path: '/complaints', element: loading(<ComplaintsFeedbackPage />) },
      { path: '/complaints-and-feedback', element: loading(<ComplaintsFeedbackPage />) },
      { path: '/about-operator', element: loading(<OperatorPage />) },
      { path: '/register', element: loading(<RegisterPage />) },
      { path: '/registration/success/:registrationId', element: loading(<RegistrationSuccessPage />) },
      { path: '/payment/return', element: loading(<PaymentReturnPage />) },
      { path: '/privacy', element: loading(<LegalPage kind="privacy" />) },
      { path: '/terms', element: loading(<LegalPage kind="terms" />) },
      { path: '/refund-policy', element: loading(<LegalPage kind="refund" />) },
      { path: '/fulfilment-policy', element: loading(<LegalPage kind="fulfilment" />) },
      { path: '/accessibility', element: loading(<LegalPage kind="accessibility" />) },
      { path: '/verify-certificate/:certificateId?', element: loading(<CertificateVerificationPage />) },
      { path: '/certificate/:certificateId', element: loading(<CertificatePrintPage />) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '/finale/leaderboard', element: <Navigate to="/competition" replace /> },
  { path: '/portal/sign-in', element: loading(<ParticipantSignInPage />) },
  { path: '/portal/signin', element: <Navigate to="/portal/sign-in" replace /> },
  {
    path: '/portal',
    element: loading(<ParticipantPortalBoundary />),
    children: [
      { index: true, element: loading(<ParticipantDashboardPage />) },
      { path: 'profile', element: loading(<ParticipantProfilePage />) },
      { path: 'notifications', element: loading(<ParticipantNotificationsPage />) },
      { path: 'certificates', element: loading(<ParticipantCertificatesPage />) },
      { path: 'results', element: loading(<ParticipantResultsPage />) },
      { path: 'quiz/:levelCode/start', element: loading(<ParticipantQuizStartPage />) },
      { path: 'quiz/session/:sessionId', element: loading(<ParticipantQuizPage />) },
      { path: 'quiz/submitted', element: loading(<ParticipantQuizSubmittedPage />) },
    ],
  },
  { path: '/admin/sign-in', element: loading(<AdminSignInPage />) },
  {
    path: '/admin',
    element: loading(<AdminBoundary />),
    children: [
      { index: true, element: loading(<AdminIndexPage />) },
      { path: 'operations', element: loading(<AdminOperationsPage />) },
      { path: 'questions', element: loading(<AdminQuestionsPage />) },
      { path: 'questions/new', element: loading(<AdminQuestionEditorPage />) },
      { path: 'questions/import', element: loading(<AdminQuestionImportPage />) },
      { path: 'questions/:questionId', element: loading(<AdminQuestionEditorPage />) },
      { path: ':section', element: loading(<AdminWorkspacePage />) },
    ],
  },
]);
